import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { RefObject, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const formatVideoTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainder = totalSeconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
};

export const VideoPlaybackControls = ({
  videoRef,
  className,
}: {
  videoRef: RefObject<HTMLVideoElement | null>;
  className?: string;
}) => {
  const [isPaused, setIsPaused] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    const syncFromVideo = () => {
      setIsPaused(video.paused);
      setIsMuted(video.muted);
      setCurrentTime(video.currentTime);
      setDuration(Number.isFinite(video.duration) ? video.duration : 0);
    };

    syncFromVideo();
    video.addEventListener("play", syncFromVideo);
    video.addEventListener("pause", syncFromVideo);
    video.addEventListener("timeupdate", syncFromVideo);
    video.addEventListener("loadedmetadata", syncFromVideo);
    video.addEventListener("volumechange", syncFromVideo);

    return () => {
      video.removeEventListener("play", syncFromVideo);
      video.removeEventListener("pause", syncFromVideo);
      video.removeEventListener("timeupdate", syncFromVideo);
      video.removeEventListener("loadedmetadata", syncFromVideo);
      video.removeEventListener("volumechange", syncFromVideo);
    };
  }, [videoRef]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      void video.play();
      return;
    }
    video.pause();
  };

  const toggleMuted = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.muted = !video.muted;
  };

  const seekTo = (value: number) => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.currentTime = value;
  };

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5",
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="tinyIcon"
        onClick={togglePlayback}
        className="h-7 w-7 shrink-0 text-gray-700 hover:bg-gray-200"
        aria-label={isPaused ? "Play" : "Pause"}
      >
        {isPaused ? (
          <Play className="h-3.5 w-3.5 fill-current" />
        ) : (
          <Pause className="h-3.5 w-3.5" />
        )}
      </Button>
      <span className="min-w-[4.5rem] shrink-0 font-mono text-[11px] text-gray-600">
        {formatVideoTime(currentTime)} / {formatVideoTime(duration)}
      </span>
      <input
        type="range"
        min={0}
        max={duration || 0}
        step={0.1}
        value={Math.min(currentTime, duration || 0)}
        onChange={event => seekTo(Number(event.target.value))}
        className="h-1.5 min-w-0 flex-1 cursor-pointer accent-gray-700"
        aria-label="Seek"
      />
      <Button
        type="button"
        variant="ghost"
        size="tinyIcon"
        onClick={toggleMuted}
        className="h-7 w-7 shrink-0 text-gray-700 hover:bg-gray-200"
        aria-label={isMuted ? "Unmute" : "Mute"}
      >
        {isMuted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
};
